import { useMemo } from "react";

import {
    LoopInfoParams,
    JupiterQuoteParams,
    JupiterSwapMode,
    UserLoopFilter,
    StrategyDuration,
    TokenListTag,
    TokenListMetadata,
    UncertaintyType,
    LoopRouteType,
    LoopVaultInfo,
    WindRouteStepExpanded,
    WhirlpoolPositionExpanded,
    ExtendedUnwindRouteStep,
    QuoteFetchParams,
    OracleQuote,
    ClmmPositionExpanded,
    getTokenMintFromClmm,
    LoopWindType
} from "@bridgesplit/abf-sdk";
import {
    decimalsToBps,
    filterNullableRecord,
    formatNum,
    getUnixTs,
    greaterThan,
    IS_LOCAL_NX_DEV,
    lamportsToUiAmount,
    NullableRecord,
    percentUiToDecimals,
    roundDownToDecimals,
    SortDirection,
    TIME,
    uiAmountToLamports,
    WRAPPED_SOL_MINT,
    bsMath,
    filterUndefined,
    filterTruthy,
    roundTimestamp,
    USDC_MINT,
    BalanceChanges,
    PandoraErrorCodes
} from "@bridgesplit/utils";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/dist/query";
import { SerializedError } from "@reduxjs/toolkit";
import { useMemoizedKeyMap, useMemoizedMap } from "@bridgesplit/ui";
import { sqrtPriceX64ToTickIndex } from "@orca-so/whirlpool-client-sdk";
import { BN } from "bn.js";

import {
    useTokenListMetadataByMints,
    useLoopBestQuoteQuery,
    useLoopInfosQuery,
    useUserLoopsQuery,
    useStatsQuery,
    useFlashPerpsQuery,
    useLoopWindQuoteIxsQuery,
    useLoopUnwindQuoteIxsQuery,
    LoopUnwindRouteRequestExtended,
    LoopRouteResponseExpanded,
    LoopWindRouteRequestExtended,
    useWhirlpoolsQuery,
    useCollateralYieldHistoryQuery,
    useLoopRolloverQuoteIxsQuery,
    useWhirlpoolsOffchainQuery,
    useLoopCollateralYieldLatestByMintsQuery
} from "../reducers";
import {
    AbfLoanExpanded,
    BestLoopQuoteQuery,
    ExternalUnwindQuoteParams,
    ExternalWindQuoteParams,
    LoopExpanded,
    LoopPositionExpanded,
    LoopQuoteExpanded,
    LoopExpandedBase,
    AbfOrderFundingType,
    PriceFetchType,
    LoopExpandedMeteora,
    ParsedLoanEventExpanded,
    LoanEventActionType,
    LiquidationType,
    LoanInfoSorting,
    LoopExpandedOrca,
    LoopTokenPriceChange,
    LoanCollateralExpanded,
    OracleOHLC,
    LoanStatus
} from "../types";
import { useOracleHistoricalPrices, useOraclePrices } from "./pricing";
import {
    isLoopLoan,
    useAbfTypesToUiConverter,
    isLiquidateEvent,
    isWhirlpoolLoopDeposit,
    isLeverageLoop
} from "../utils";
import { calculateLoanHealth, getZcCollateral, getZcLedger, isLoanActive, useLoanInfos } from "./loans";
import { getBorrowCapDetails, useMarketBorrowCaps } from "./markets";
import { DEFAULT_STRATEGY_DURATION } from "../constants";
import { useActiveWallet } from "./wallet";
import { useEpochInfo } from "./utils";
import { getClaimableWhirlpoolFees, isWhirlpoolMetadata } from "./whirlpool";
import { useCurrentTime } from "./points";
import { isRaydiumMetadata } from "./raydium";

export function useLoopsExpanded(params: LoopInfoParams, options?: { skip?: boolean }) {
    const query = useLoopInfosQuery(params, { skip: options?.skip });

    const collateralMints = useMemo(
        () => Object.values(query.data ?? {}).map((loop) => loop.collateralMint),
        [query.data]
    );
    const principalMints = useMemo(
        () => Object.values(query.data ?? {}).map((loop) => loop.principalMint),
        [query.data]
    );

    const allMints = useMemo(() => [...collateralMints, ...principalMints], [collateralMints, principalMints]);
    const { getMetadata, isLoading: metadataLoading } = useTokenListMetadataByMints(allMints);
    const { getOracle, isLoading: marketLoading } = useOraclePrices(allMints, PriceFetchType.Twap);
    const { convertLoopVault, convertMarketPrincipalStats } = useAbfTypesToUiConverter(allMints);
    const { getBorrowCap, isLoading: borrowCapsLoading } = useMarketBorrowCaps(principalMints);

    const { data: statsQuery, isLoading: principalStatsLoading } = useStatsQuery(
        { principalMints, includePrincipal: true },
        { skip: !principalMints.length || options?.skip }
    );

    const principalStatsMap = useMemo(
        () => (statsQuery?.principal ? new Map(Object.entries(statsQuery.principal)) : undefined),
        [statsQuery]
    );

    const whirlpoolMints = useMemo(() => {
        if (!query.data) return [];
        return Object.values(query.data)
            .map((loop) => getWhirlpoolFromLoopVault(loop))
            .filter(filterUndefined);
    }, [query.data]);

    const { data: whirlpools, isLoading: whirlpoolsLoading } = useWhirlpoolsQuery(whirlpoolMints, {
        skip: !whirlpoolMints.length || options?.skip
    });

    const { data: whirlpoolsOffchain, isLoading: whirlpoolsOffchainLoading } = useWhirlpoolsOffchainQuery(
        { addresses: whirlpoolMints },
        {
            skip: !whirlpoolMints.length || options?.skip
        }
    );

    const isLoading =
        query.isLoading ||
        metadataLoading ||
        marketLoading ||
        principalStatsLoading ||
        borrowCapsLoading ||
        whirlpoolsLoading ||
        whirlpoolsOffchainLoading;

    const data = useMemo(() => {
        if (!query.data || isLoading) return undefined;

        return Object.entries(query.data)
            .map(([vaultIdentifier, loopVaultRaw]): NullableRecord<LoopExpanded> => {
                const loopVault = convertLoopVault(loopVaultRaw);
                const borrowCap = getBorrowCap(loopVault.principalMint);
                let principalStats = principalStatsMap?.get(loopVault.principalMint);
                if (principalStats) {
                    principalStats = convertMarketPrincipalStats(loopVault.principalMint, principalStats);
                }

                const inputToken =
                    getLoopWindType(loopVault) === LoopWindType.Collateral
                        ? getMetadata(loopVault.collateralMint)
                        : getMetadata(loopVault.principalMint);

                const common: NullableRecord<LoopExpandedBase> = {
                    vaultIdentifier,
                    loopVault,
                    collateralToken: getMetadata(loopVault.collateralMint),
                    principalToken: getMetadata(loopVault.principalMint),
                    principalOracle: getOracle(loopVault.principalMint, PriceFetchType.Spot),
                    collateralOracle: getOracle(loopVault.collateralMint, PriceFetchType.Spot),
                    principalStats,
                    borrowCap,
                    maxCollateralAmount: loopVault.maxCollateralAmount,
                    loopWindType: getLoopWindType(loopVault),
                    inputToken
                };

                if (!loopVault.feVisible && !params.showHidden && !IS_LOCAL_NX_DEV) {
                    return {
                        ...common,
                        // this will disable the loop in the UI
                        tokenA: undefined
                    };
                }

                const whirlpool = getWhirlpoolFromLoopVault(loopVault);
                if (whirlpool) {
                    const whirlpoolData = whirlpools?.[whirlpool];
                    // will be disable in ui
                    if (!whirlpoolData) return { ...common, whirlpool: undefined };

                    return {
                        ...common,
                        collateralOracle: null,
                        whirlpool: {
                            ...whirlpoolData,
                            tokenA: getMetadata(whirlpoolData.tokenMintA),
                            tokenB: getMetadata(whirlpoolData.tokenMintB)
                        },
                        whirlpoolOffchain: whirlpoolsOffchain?.data?.find((w) => w.address === whirlpool)
                    };
                }

                if (loopVault.metadata && "lpMint" in loopVault.metadata) {
                    const tokenA = getMetadata(loopVault.metadata.tokenAMint);
                    const tokenB = getMetadata(loopVault.metadata.tokenBMint);
                    return {
                        ...common,
                        meteoraPool: loopVault.metadata,
                        tokenA,
                        tokenB
                    };
                }

                return {
                    ...common
                };
            })
            .filter(filterNullableRecord);
    }, [
        query.data,
        isLoading,
        convertLoopVault,
        getBorrowCap,
        principalStatsMap,
        getMetadata,
        getOracle,
        params.showHidden,
        convertMarketPrincipalStats,
        whirlpools,
        whirlpoolsOffchain
    ]);

    const filteredData = useMemo(() => {
        if (!data) return undefined;
        if (params.showHidden) return data;
        return data.filter((loop) => loop.loopVault.feVisible);
    }, [data, params.showHidden]);

    return { data: filteredData, isLoading, isFetching: query.isFetching };
}

export function useUserLoops({ filter, skip }: { filter: UserLoopFilter; skip?: boolean }): {
    data: LoopPositionExpanded[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const query = useUserLoopsQuery(
        {
            ...filter
        },
        {
            skip: skip
        }
    );

    const loopAddresses = useMemo(() => {
        return query?.data?.map((loop) => loop.loopVaultIdentifier);
    }, [query?.data]);

    const {
        data: loopsExpanded,
        isLoading: isLoopExtendedLoading,
        isFetching: isLoopExtendedFetching
    } = useLoopsExpanded({ showHidden: true, loopVaults: loopAddresses }, { skip });

    const loanAddresses = useMemo(() => {
        return query.data ? query.data.map((userLoop) => userLoop.loanAddress) : [];
    }, [query?.data]);

    const {
        data: loanInfos,
        isLoading: loanInfosLoading,
        isFetching: loanInfosFetching
    } = useLoanInfos({
        skip: !loanAddresses.length,
        pagination: null,
        loanFilter: {
            loanAddresses,
            orderFundingTypes: AbfOrderFundingType.Loop,
            borrowers: [],
            lenders: [],
            principalMints: [],
            sortSide: SortDirection.Asc,
            collateralMints: [],
            sortType: LoanInfoSorting.StartTime
        }
    });

    const loanExpandedMap = useMemoizedKeyMap(loanInfos, (l) => l.loan.address);
    const loopExpandedMap = useMemoizedKeyMap(loopsExpanded, (l) => l.vaultIdentifier);

    const { convertUserLoopInfo } = useAbfTypesToUiConverter([]);
    let earliestAddCollateralEventTime = Infinity;

    const firstAddCollateralMints = loanInfos
        ?.map((l) => {
            if (isLoopLoan(l)) {
                const firstAddCollateralEvent = getLoopFirstAddCollateralEvent(l.events);
                earliestAddCollateralEventTime = Math.min(
                    earliestAddCollateralEventTime,
                    firstAddCollateralEvent?.eventTime ?? 0
                );

                return firstAddCollateralEvent?.assetMint;
            }
            return undefined;
        })
        .filter(filterTruthy);

    const { data: oracleHistoricalPrices } = useOracleHistoricalPrices({
        mints: Array.from(new Set(firstAddCollateralMints)),
        startTimestamp: earliestAddCollateralEventTime
    });

    // Get collateral yields and oracle prices for whirlpool calculations
    const collateralMints = useMemo(() => {
        const mints: string[] = [];
        if (query.data && loanInfos) {
            query.data.forEach((userLoop) => {
                const loanExpanded = loanExpandedMap?.get(userLoop.loanAddress);
                if (loanExpanded) {
                    mints.push(...getTokenMintsFromClmmCollateral(loanExpanded));
                    mints.push(...loanExpanded.collateral.map((c) => c.assetMint));
                    mints.push(loanExpanded.principalMetadata.mint);
                }
            });
        }
        return [...new Set(mints)]; // Remove duplicates
    }, [query.data, loanInfos, loanExpandedMap]);

    const {
        collateralYields: collateralYieldsMap,
        oraclePrices: oraclePricesMap,
        isLoading: underlyingTokenLoading
    } = useUnderlyingTokenInfo(collateralMints);

    const isLoading = query.isLoading || loanInfosLoading || underlyingTokenLoading || isLoopExtendedLoading;
    const isFetching = query.isFetching || loanInfosFetching || isLoopExtendedFetching;

    const data = useMemo(() => {
        if (isLoading || isFetching) return undefined;
        if (skip || !query.data || !loanInfos || !loopsExpanded) return [];
        const data = query.data.map((userLoop): LoopPositionExpanded | undefined => {
            const loanExpanded = loanExpandedMap?.get(userLoop.loanAddress);
            const loopExpanded = loopExpandedMap?.get(userLoop.loopVaultIdentifier);
            const userLoopInfo = convertUserLoopInfo(
                userLoop,
                loanExpanded,
                oracleHistoricalPrices,
                loopExpanded,
                collateralYieldsMap,
                oraclePricesMap
            );
            if (!userLoopInfo || !loanExpanded) return undefined;
            return {
                loanAddress: userLoop.loanAddress,
                userLoopInfo,
                loanExpanded,
                loopExpanded,
                allowedWindTypes: getAllowedWindTypes(loopExpanded) ?? []
            };
        });

        return data.filter(filterUndefined);
    }, [
        isLoading,
        loopsExpanded,
        isFetching,
        skip,
        query.data,
        loanInfos,
        loanExpandedMap,
        loopExpandedMap,
        convertUserLoopInfo,
        oracleHistoricalPrices,
        collateralYieldsMap,
        oraclePricesMap
    ]);

    return { data, isLoading, isFetching };
}

export function getAllowedWindTypes(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;
    if (loopExpanded.loopVault.routeType === LoopRouteType.Orca) {
        return [LoopWindType.Principal];
    }
    return [LoopWindType.Collateral, LoopWindType.Principal];
}

export function useFlashPerps(options?: { skip?: boolean }) {
    return useFlashPerpsQuery(undefined, { skip: options?.skip });
}

export function useUserLoopByLoan(loanExpanded: AbfLoanExpanded | undefined, options?: { skip?: boolean }) {
    const positionQuery = useUserLoopsQuery(
        {
            page: 0,
            pageSize: 1,
            active: isLoanActive(loanExpanded),
            loanVaults: loanExpanded ? [loanExpanded.loan.address] : [],
            loopVaults: []
        },
        {
            skip: options?.skip || !loanExpanded || !isLoopLoan(loanExpanded)
        }
    );

    const loopPosition = positionQuery.data?.find((userLoop) => userLoop.loanAddress === loanExpanded?.loan.address);
    const loopQuery = useLoopsExpanded(
        { loopVaults: loopPosition ? [loopPosition.loopVaultIdentifier] : undefined, showHidden: true },
        { skip: !loopPosition }
    );
    const loopExpanded = loopQuery.data ? Object.values(loopQuery.data)[0] : undefined;

    const isLoading = positionQuery.isLoading || loopQuery.isLoading;
    const isFetching = positionQuery.isFetching || loopQuery.isFetching;

    const { convertUserLoopInfo } = useAbfTypesToUiConverter([]);

    const firstAddCollateralEvent = getLoopFirstAddCollateralEvent(loanExpanded?.events);

    const { data: oracleHistoricalPrices } = useOracleHistoricalPrices({
        mints: firstAddCollateralEvent?.assetMint ? [firstAddCollateralEvent.assetMint] : [],
        startTimestamp: firstAddCollateralEvent?.eventTime ?? 0
    });

    // Get collateral yields and oracle prices for whirlpool calculations
    const loopMints = useMemo(() => {
        const mints: string[] = [];
        if (loanExpanded) {
            // Get whirlpool token mints from loan collateral
            loanExpanded.collateral.forEach((collateral: LoanCollateralExpanded) => {
                if (isWhirlpoolMetadata(collateral.metadata) || isRaydiumMetadata(collateral.metadata)) {
                    const position = collateral.clmmPosition;
                    if (position?.tokenA?.mint) {
                        mints.push(position.tokenA.mint);
                    }
                    if (position?.tokenB?.mint) {
                        mints.push(position.tokenB.mint);
                    }
                }
                mints.push(collateral.assetMint);
                mints.push(loanExpanded.principalMetadata.mint);
            });
        }
        return [...new Set(mints)]; // Remove duplicates
    }, [loanExpanded]);

    const { collateralYields, oraclePrices } = useUnderlyingTokenInfo(loopMints);

    const data = useMemo((): LoopPositionExpanded | undefined => {
        if (!loanExpanded || !loopPosition || isLoading) return undefined;
        const userLoopInfo = convertUserLoopInfo(
            loopPosition,
            loanExpanded,
            oracleHistoricalPrices,
            loopExpanded,
            collateralYields,
            oraclePrices
        );
        const allowedWindTypes = getAllowedWindTypes(loopExpanded);
        if (!allowedWindTypes || !userLoopInfo) return undefined;
        return {
            loanAddress: loanExpanded.loan.address,
            userLoopInfo,
            loanExpanded,
            loopExpanded,
            allowedWindTypes
        };
    }, [
        loanExpanded,
        loopPosition,
        isLoading,
        loopExpanded,
        convertUserLoopInfo,
        oracleHistoricalPrices,
        collateralYields,
        oraclePrices
    ]);

    return { data, isLoading, isFetching };
}

export function formatLeverage(leverage: number, decimals = 1) {
    return `${formatNum(leverage, { customDecimals: decimals })}x`;
}

export function getLoopWindType(loop: LoopVaultInfo) {
    if (loop.name.toLowerCase().includes("orca")) {
        return LoopWindType.Principal;
    }
    return LoopWindType.Collateral;
}

export function useWindQuoteAndIxs({
    loopExpanded,
    principalAmount,
    skip,
    slippagePercentDecimals,
    windType,
    inputAmount,
    routeOverrideParams,
    clmmPosition,
    inputToken
}: ExternalWindQuoteParams) {
    const { activeWallet } = useActiveWallet();
    const request = useMemo(() => {
        if (
            !loopExpanded ||
            !principalAmount ||
            !slippagePercentDecimals ||
            !activeWallet ||
            skip ||
            inputAmount === undefined
        ) {
            return undefined;
        }

        let principalAmountToLoop = principalAmount;

        const inputMint = getTokenMintFromClmm(clmmPosition) ?? loopExpanded.principalToken.mint;

        if (
            isPrincipalTokenWind(loopExpanded.principalToken.mint, inputToken?.mint) ||
            loopExpanded.inputToken.mint === loopExpanded.principalToken.mint
        ) {
            if (clmmPosition) {
                principalAmountToLoop = principalAmount + inputAmount / loopExpanded.principalOracle.usdPrice;
            } else {
                principalAmountToLoop = principalAmount + inputAmount;
            }
        }

        const routeStep = {
            inputAmount: uiAmountToLamports(principalAmountToLoop, loopExpanded.principalToken.decimals),
            ...loopExpanded?.loopVault.quoteFetchParams,
            inputMint,
            outputMint: loopExpanded.collateralToken.mint,
            ...(routeOverrideParams ? { ...routeOverrideParams } : {})
        } as WindRouteStepExpanded;
        const request: LoopWindRouteRequestExtended = {
            slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
            userWallet: activeWallet,
            routeSteps: [
                {
                    ...routeStep
                }
            ],
            windType
        };
        return request;
    }, [
        loopExpanded,
        principalAmount,
        slippagePercentDecimals,
        activeWallet,
        skip,
        inputAmount,
        clmmPosition,
        routeOverrideParams,
        inputToken,
        windType
    ]);

    return useLoopWindQuoteIxsQuery(request ?? skipToken, {
        skip: !request || skip
    });
}

export function getWhirlpoolFromLoopVault(loopVault: LoopVaultInfo) {
    if (loopVault.routeType === LoopRouteType.Orca) {
        const routeParams = loopVault.quoteFetchParams as any;
        if (routeParams.type === "orca") {
            return routeParams.whirlpool;
        }
    }
    return undefined;
}

const UNWIND_REFETCH_INTERVAL = TIME.MINUTE * 2000;

function constructUnwindQuoteFetchParams(
    loopPositionExpanded: LoopPositionExpanded | undefined,
    rolloverQuoteFetchParams: any,
    defaultQuoteFetchParams: QuoteFetchParams | undefined
): any {
    const quoteFetchParamsFallback = {
        type: "jupiter",
        maxAccounts: 40,
        onlyDirectRoutes: true,
        restrictIntermediateTokens: true
    };
    // if this is called by a non loop - then its a loan getting a quote for its repay collateral
    if (!loopPositionExpanded) {
        return defaultQuoteFetchParams ?? quoteFetchParamsFallback;
    }

    const originalQuoteFetchParams = loopPositionExpanded.loopExpanded?.loopVault.quoteFetchParams as any;

    // Handle rollover case first (positive condition)
    if (rolloverQuoteFetchParams) {
        return {
            ...loopPositionExpanded.loopExpanded?.loopVault.quoteFetchParams,
            market: rolloverQuoteFetchParams.market,
            ptMint: rolloverQuoteFetchParams.ptMint,
            baseMint: rolloverQuoteFetchParams.baseMint,
            swapType: rolloverQuoteFetchParams.swapType,
            maturedMarket: originalQuoteFetchParams.market,
            maturedPtMint: originalQuoteFetchParams.ptMint,
            version: rolloverQuoteFetchParams.version,
            maturedVersion: rolloverQuoteFetchParams.maturedVersion,
            pool: rolloverQuoteFetchParams.pool,
            luts: [...new Set([...originalQuoteFetchParams.luts, ...rolloverQuoteFetchParams.luts])],
            ...(rolloverQuoteFetchParams.primarySwapParams && {
                primarySwapParams: rolloverQuoteFetchParams.primarySwapParams
            })
        };
    }

    // Handle orca position case
    if (originalQuoteFetchParams?.type === "orca") {
        const orcaPosition = getWhirlpoolLoopCollateral(loopPositionExpanded);
        const tickUpper =
            orcaPosition?.position.upperSqrtPrice &&
            sqrtPriceX64ToTickIndex(new BN(orcaPosition.position.upperSqrtPrice));
        const tickLower =
            orcaPosition?.position.lowerSqrtPrice &&
            sqrtPriceX64ToTickIndex(new BN(orcaPosition.position.lowerSqrtPrice));

        if (tickUpper && tickLower) {
            return { ...originalQuoteFetchParams, tickUpper, tickLower };
        }
    }

    return originalQuoteFetchParams;
}

export function useUnwindQuoteAndIxs({
    loopPositionExpanded,
    skip,
    slippagePercentDecimals,
    windType,
    rolloverLoopQuoteFetchParams,
    loanCollateral,
    loanExpanded,
    debtToRepay,
    collateralAmount // have to specify the amount of collateral for repay with collateral cases. other callers will passing full collateral amount
}: ExternalUnwindQuoteParams) {
    const { activeWallet } = useActiveWallet();
    const quoteFetchParamsMap = useGenerateQuoteFetchParamsMap();

    const request = useMemo(() => {
        if (
            !loanExpanded ||
            !collateralAmount ||
            !loanCollateral ||
            !slippagePercentDecimals ||
            !activeWallet ||
            skip ||
            windType === undefined ||
            debtToRepay === undefined
        )
            return undefined;

        const collateralMetadata = loanCollateral?.metadata;
        const collateralAmountLamports = uiAmountToLamports(collateralAmount, collateralMetadata.decimals);
        const defaultQuoteFetchParams = getDefaultQuoteFetchParams(
            quoteFetchParamsMap,
            loanCollateral?.assetIdentifier,
            loanExpanded?.principalMetadata.mint
        );

        const ledger = getZcLedger(loanExpanded);
        if (!ledger) return undefined;

        const principalTokenMetadata = loanExpanded.principalMetadata;

        const debtToRepayLamports = uiAmountToLamports(debtToRepay, principalTokenMetadata.decimals);

        const routeStep: ExtendedUnwindRouteStep = {
            principalDecimals: principalTokenMetadata.decimals,
            collateralDecimals: collateralMetadata.decimals,
            ledgerDebtAmount: debtToRepayLamports,
            totalCollateralAmount: collateralAmountLamports,
            inputMint: loanCollateral?.assetMint,
            outputMint: principalTokenMetadata.mint,
            ...constructUnwindQuoteFetchParams(
                loopPositionExpanded,
                rolloverLoopQuoteFetchParams,
                defaultQuoteFetchParams
            )
        };
        const request: LoopUnwindRouteRequestExtended = {
            slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
            userWallet: activeWallet,
            routeSteps: [routeStep],
            loanAddress: loanExpanded.loan.address,
            windType
        };
        return request;
    }, [
        loopPositionExpanded,
        slippagePercentDecimals,
        activeWallet,
        skip,
        windType,
        rolloverLoopQuoteFetchParams,
        loanExpanded,
        loanCollateral,
        debtToRepay,
        collateralAmount,
        quoteFetchParamsMap
    ]);

    const hasRolloverLoop = !!rolloverLoopQuoteFetchParams;

    const unwindQuery = useLoopUnwindQuoteIxsQuery(request ?? skipToken, {
        skip: !request || skip || hasRolloverLoop,
        refetchOnMountOrArgChange: true,
        pollingInterval: UNWIND_REFETCH_INTERVAL
    });

    const rolloverQuery = useLoopRolloverQuoteIxsQuery(request ?? skipToken, {
        skip: !request || skip || !hasRolloverLoop,
        refetchOnMountOrArgChange: true,
        pollingInterval: UNWIND_REFETCH_INTERVAL
    });

    return hasRolloverLoop ? rolloverQuery : unwindQuery;
}

function getDefaultQuoteFetchParams(
    quoteFetchParamsMap: Record<string, Record<string, QuoteFetchParams>> | undefined,
    collateralIdentifier: string | undefined,
    principalMint: string | undefined
): QuoteFetchParams | undefined {
    if (!quoteFetchParamsMap || !collateralIdentifier || !principalMint) return undefined;

    const collateralQuoteFetchParams = quoteFetchParamsMap[collateralIdentifier];
    if (!collateralQuoteFetchParams) return undefined;

    // Try exact match first, then fallback to first available
    return collateralQuoteFetchParams[principalMint] ?? Object.values(collateralQuoteFetchParams)[0];
}

// collateralMint -> principalMint -> quoteFetchParams
// do the nested mapping to have a fallback if a collateral-principal pair is not found
function useGenerateQuoteFetchParamsMap(): Record<string, Record<string, QuoteFetchParams>> | undefined {
    const { data: loops } = useLoopsExpanded({
        showHidden: true
    });

    const quoteFetchParamsMap = useMemo(() => {
        return loops?.reduce((acc, loop) => {
            const collateralMint = loop.loopVault.collateralMint;
            const principalMint = loop.loopVault.principalMint;

            if (!acc[collateralMint]) {
                acc[collateralMint] = {};
            }
            acc[collateralMint][principalMint] = loop.loopVault.quoteFetchParams;
            return acc;
        }, {} as Record<string, Record<string, QuoteFetchParams>>);
    }, [loops]);
    return quoteFetchParamsMap;
}

export const MAX_JUP_ACCOUNTS = 36;

export const DEFAULT_JUP_QUOTE_PARAMS: Omit<
    JupiterQuoteParams,
    "amount" | "slippageBps" | "inputMint" | "outputMint" | "outputMint"
> = {
    maxAccounts: MAX_JUP_ACCOUNTS,
    restrictIntermediateTokens: true,
    swapMode: JupiterSwapMode.ExactIn,
    onlyDirectRoutes: false
};

export function calculateLtvFromLeverage(leverageMultiplier: number) {
    return 1 - 1 / leverageMultiplier;
}

export function getLoopLeveragedAmounts({
    inputToken,
    principalToken,
    collateralToken,
    leverageMultiplier,
    inputAmount,
    clmmPosition,
    oracleQuotes,
    loopExpanded
}: {
    inputToken: TokenListMetadata;
    principalToken: TokenListMetadata;
    leverageMultiplier: number;
    inputAmount: number;
    clmmPosition: ClmmPositionExpanded | undefined;
    oracleQuotes?: Map<string, OracleQuote | undefined>;
    collateralToken: TokenListMetadata;
    loopExpanded: LoopExpanded | undefined;
}) {
    const isOrcaRoute = loopExpanded?.loopVault.routeType === LoopRouteType.Orca;
    const isPrincipalWind = isPrincipalTokenWind(principalToken.mint, inputToken.mint) && !isOrcaRoute;
    const leveragedInputAmount = inputAmount * leverageMultiplier;
    const inputOracle = oracleQuotes?.get(inputToken.mint);
    const principalOracle = oracleQuotes?.get(principalToken.mint);
    const collateralOracle = oracleQuotes?.get(collateralToken.mint);

    const leveragedInputAmountUsd = clmmPosition
        ? clmmPosition.totalPrice * leverageMultiplier
        : inputOracle?.getUsdAmount(leveragedInputAmount, undefined, UncertaintyType.Subtract);

    if (leveragedInputAmountUsd === undefined || principalOracle === undefined) {
        return {
            principalAmount: 0,
            leveragedCollateralAmount: 0
        };
    }
    const principalUsd = leveragedInputAmountUsd * calculateLtvFromLeverage(leverageMultiplier);
    const principalAmount = roundDownToDecimals(
        principalUsd / (principalOracle.usdPrice + principalOracle.uncertainty),
        principalToken.decimals
    );

    const leveragedCollateralAmount = isPrincipalWind
        ? leveragedInputAmountUsd / (collateralOracle?.usdPrice ?? 0)
        : leveragedInputAmount;

    return {
        principalAmount,
        leveragedCollateralAmount
    };
}

type LoopBestQuoteParams = {
    loopExpanded: LoopExpanded | undefined;
    collateralAmountUi: number | undefined;
    inputAmountUi: number | undefined;
    inputToken: TokenListMetadata | undefined;
    leverageMultiplier: number | undefined;
    slippagePctDecimals?: number;
    priceOverride?: number;
    strategyDuration: StrategyDuration;
    skip?: boolean;
    oracleQuotes?: Map<string, OracleQuote | undefined>;
    clmmPosition: ClmmPositionExpanded | undefined;
};
export function useLoopBestQuote(params: LoopBestQuoteParams) {
    const { convertOfferQuote } = useAbfTypesToUiConverter([]);
    const quoteParams = useMemo((): BestLoopQuoteQuery | undefined => {
        if (
            !params.loopExpanded ||
            !params.collateralAmountUi ||
            !isValidLeverage(params.leverageMultiplier) ||
            !params.strategyDuration
        )
            return undefined;
        return {
            principalMint: params.loopExpanded?.principalToken.mint,
            collateralInfo: {
                assetIdentifier: params.loopExpanded?.collateralToken.mint,
                amount: uiAmountToLamports(params.collateralAmountUi, params.loopExpanded?.collateralToken.decimals),
                assetMint: params.loopExpanded?.collateralToken.mint,
                assetType: params.loopExpanded?.collateralToken.assetType,
                priceOverride: params.priceOverride
            },
            duration: params.strategyDuration,
            leverageMultiplier: params.leverageMultiplier
        };
    }, [params]);

    const skip = !quoteParams || params.skip;
    const { data: rawData, ...query } = useLoopBestQuoteQuery(quoteParams ?? skipToken, {
        skip
    });

    const data = useMemo((): LoopQuoteExpanded | null | undefined => {
        if (!params.loopExpanded || !quoteParams || !params.collateralAmountUi || !params.inputAmountUi)
            return undefined;
        if (!rawData) return rawData;
        const collateralAmount = params.collateralAmountUi;
        const inputAmount = params.inputAmountUi;
        const bestQuote = convertOfferQuote(rawData.bestQuote, params.loopExpanded?.principalToken.mint);
        const { principalAmount, leveragedCollateralAmount } = getLoopLeveragedAmounts({
            principalToken: params.loopExpanded.principalToken,
            collateralToken: params.loopExpanded.collateralToken,
            inputToken: params.inputToken ?? params.loopExpanded.inputToken,
            leverageMultiplier: quoteParams.leverageMultiplier - (params.slippagePctDecimals ?? 0.05),
            inputAmount,
            clmmPosition: params.clmmPosition,
            oracleQuotes: params.oracleQuotes,
            loopExpanded: params.loopExpanded
        });
        return {
            ...rawData,
            netApyPct: percentUiToDecimals(rawData.netApyPct),
            bestQuote,
            leverageMultiplier: quoteParams.leverageMultiplier - (params.slippagePctDecimals ?? 0.05),
            collateralAmount,
            principalAmount,
            leveragedCollateralAmount
        };
    }, [quoteParams, rawData, convertOfferQuote, params]);

    return { data, ...query };
}

export function usePrincipalBorrowedWithLeverage({
    loopExpanded,
    loopLeverage,
    totalAmount,
    skip = false,
    clmmPosition,
    oracleQuotes
}: {
    loopExpanded: LoopExpanded | undefined;
    loopLeverage: number | undefined;
    totalAmount: number;
    skip?: boolean;
    clmmPosition: ClmmPositionExpanded | undefined;
    oracleQuotes?: Map<string, OracleQuote | undefined>;
}) {
    const quoteParams = useMemo((): LoopBestQuoteParams => {
        return {
            loopExpanded,
            leverageMultiplier: loopLeverage,
            inputAmountUi: totalAmount,
            collateralAmountUi: totalAmount,
            strategyDuration: DEFAULT_STRATEGY_DURATION,
            clmmPosition,
            oracleQuotes,
            inputToken: loopExpanded?.inputToken
        };
    }, [loopExpanded, loopLeverage, totalAmount, clmmPosition, oracleQuotes]);

    const { data: quote, isLoading: isQuoteLoading } = useLoopBestQuote({
        ...quoteParams,
        skip
    });

    const principalBorrowedWithLeverageUsd = useMemo(() => {
        const principalOracle = oracleQuotes?.get(loopExpanded?.principalToken.mint ?? "");
        if (!quote?.principalAmount || principalOracle?.usdPrice === undefined) {
            return undefined;
        }
        return quote.principalAmount * principalOracle.usdPrice;
    }, [quote, oracleQuotes, loopExpanded]);

    return {
        principalBorrowedWithLeverageUsd,
        isLoading: isQuoteLoading
    };
}

export function calculateLoopNetApyFromMultiplier({
    collateralYield,
    leverageMultiplier,
    principalBorrowRate
}: {
    collateralYield: number;
    leverageMultiplier: number;
    principalBorrowRate: number;
}) {
    const collateralWeighted = leverageMultiplier;
    const principalWeighted = collateralWeighted * calculateLtvFromLeverage(leverageMultiplier);
    const netApy = collateralWeighted * collateralYield - principalWeighted * principalBorrowRate;
    return netApy;
}

export function getDerivedLoopAssetMints(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;
    return [loopExpanded.collateralToken.mint, loopExpanded.principalToken.mint];
}

export function getLoopBorrowCapDetails(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;
    return getBorrowCapDetails({
        borrowCap: loopExpanded.borrowCap,
        token: loopExpanded.principalToken,
        principalUtilized: loopExpanded.principalStats.principalUtilized
    });
}

export function useAllUserLoops(filter: UserLoopFilter) {
    const { activeWallet } = useActiveWallet();

    return useUserLoops({
        filter,
        skip: !activeWallet
    });
}

const TRENDING_LOOPS_LIMIT = 3;
const TRENDING_LOOPS_MIN_DEPOSIT_THRESHOLD = 25_000; // $15k minimum deposits to be displayed
export function useTrendingLoops() {
    const { data: loops } = useLoopsExpanded({
        showHidden: false
    });

    const trendingLoops: LoopExpanded[] | undefined = useMemo(() => {
        if (!loops) return undefined;

        return loops
            .filter((loop) => loop.loopVault.oneWeekCollateralDepositsUsd !== undefined)
            .filter((loop) => loop.loopVault.collateralDepositedUsd >= TRENDING_LOOPS_MIN_DEPOSIT_THRESHOLD)
            .sort(
                (a, b) =>
                    (b.loopVault.oneWeekCollateralDepositsUsd ?? 0) - (a.loopVault.oneWeekCollateralDepositsUsd ?? 0)
            )
            .slice(0, TRENDING_LOOPS_LIMIT);
    }, [loops]);

    return { data: trendingLoops, isLoading: false };
}

// leverage must be at least 1 to start a loan
export const isValidLeverage = (leverageMultiplier: number | undefined): leverageMultiplier is number =>
    greaterThan(leverageMultiplier, 1);

export interface UnwindTokenStats {
    balanceChanges: BalanceChanges | undefined;
    tokenPrices: Record<string, number> | undefined;
    receiveUsdAmount: number;
    receiveCollateralTokenAmount: number;
}

export interface ProfitLossStats {
    profitLossUsd: number;
    profitLossTokenAmount: number | undefined;
    contributions: ReturnType<typeof sumLoopPositionUsdContributions>;
    tokenStats?: UnwindTokenStats | null;
    feeDecimals?: number;
    priceImpactDecimals?: number;
}

const LOAN_CLOSE_SOL_BALANCE = 12400022;

export function useUnwindStats({
    loopPosition,
    externalQuote,
    windType,
    previewBalances
}: {
    loopPosition: LoopPositionExpanded | undefined;
    externalQuote: LoopRouteResponseExpanded | undefined;
    windType: LoopWindType | undefined;
    previewBalances: BalanceChanges | undefined;
}): ProfitLossStats | undefined {
    const allMints = previewBalances ? Object.keys(previewBalances) : [];
    const { getMetadata } = useTokenListMetadataByMints(allMints);
    const { getOracle } = useOraclePrices(allMints);

    if (!loopPosition || !externalQuote || !previewBalances) return undefined;

    const contributions = sumLoopPositionUsdContributions(loopPosition);
    const collateralMint = getZcCollateral(loopPosition.loanExpanded)?.assetIdentifier;
    const tokenPrices = Object.fromEntries(allMints.map((mint) => [mint, getOracle(mint)?.marketPrice ?? 0]));

    // divide amount with decimals and remove mints with no metadata
    // remove sol balance if < LOAN_CLOSE_SOL_BALANCE
    previewBalances =
        previewBalances &&
        Object.fromEntries(
            Object.entries(previewBalances)
                .map(([mint, amount]) => {
                    const metadata = getMetadata(mint);
                    return metadata ? [mint, lamportsToUiAmount(amount, metadata.decimals)] : null;
                })
                .filter((entry): entry is [string, number] => entry !== null)
        );

    if (
        previewBalances &&
        previewBalances[WRAPPED_SOL_MINT] <
            lamportsToUiAmount(LOAN_CLOSE_SOL_BALANCE, getMetadata(WRAPPED_SOL_MINT)?.decimals || 0)
    ) {
        delete previewBalances[WRAPPED_SOL_MINT];
    }

    const receiveUsdAmount = previewBalances
        ? Object.entries(previewBalances).reduce((sum, [mint, amount]) => {
              const price = tokenPrices[mint] ?? 0;
              return sum + amount * price;
          }, 0)
        : 0;

    const receiveCollateralTokenAmount = collateralMint ? previewBalances?.[collateralMint] : 0;

    const tokenStats: UnwindTokenStats = {
        balanceChanges: previewBalances,
        tokenPrices,
        receiveUsdAmount,
        receiveCollateralTokenAmount
    };

    const profitLossUsd = tokenStats.receiveUsdAmount - contributions.totalContributionUsd;
    let profitLossTokenAmount = undefined;
    if (windType !== LoopWindType.Principal) {
        profitLossTokenAmount =
            tokenStats.receiveCollateralTokenAmount - contributions.totalCollateralContributionAmount;
    }

    return {
        tokenStats,
        profitLossUsd,
        profitLossTokenAmount,
        contributions,
        feeDecimals: externalQuote?.feeDecimals ?? 0,
        priceImpactDecimals: externalQuote?.priceImpactDecimals
    };
}

interface HistoricalNpv {
    npvUsd: number | undefined;
    npvTokenAmount: number | undefined;
}

function getHistoricalNpvFromWithdrawalInfo(position: LoopPositionExpanded): HistoricalNpv | undefined {
    const withdrawalInfo = position.userLoopInfo.withdrawalInfo;
    if (!withdrawalInfo) {
        return undefined;
    }
    const withdrawalTokenAmount = withdrawalInfo.withdrawnTokenAmount;

    const lastCloseEvents = getLastCloseEvents(position.loanExpanded.events);
    const lastRemoveCollateralEvent = lastCloseEvents?.lastRemoveCollateralEvent;
    const lastRepayPrincipalEvent = lastCloseEvents?.lastRepayPrincipalEvent;

    const withdrawalTokenPrice =
        withdrawalInfo.withdrawnTokenMint === position.loopExpanded?.collateralToken.mint
            ? lastRemoveCollateralEvent?.usdPrice
            : lastRepayPrincipalEvent?.usdPrice;

    const withdrawalTokenUsd = bsMath.mul(withdrawalTokenAmount, withdrawalTokenPrice);

    return {
        npvUsd: withdrawalTokenUsd,
        npvTokenAmount: withdrawalTokenAmount
    };
}

export function calculateHistoricalLoopProfitLoss(position: LoopPositionExpanded): ProfitLossStats {
    const oracleProfitLoss = calculateHistoricalOracleProfitLossStats(position);

    return {
        profitLossUsd: oracleProfitLoss?.usdProfitLoss ?? 0,
        contributions: sumLoopPositionUsdContributions(position),
        profitLossTokenAmount: oracleProfitLoss?.profitLossTokenAmount
    };
}

interface TokenProfitLossValues {
    netCollateralTransfer: EventTokenChangeSummary | undefined;
    additionalBorrows: EventTokenChangeSummary | undefined;
    additionalRepayments: EventTokenChangeSummary | undefined;
    profitLossTokenAmount: number | undefined;
    valueOfAdditionalBorrows: number | undefined;
    valueOfAdditionalRepayments: number | undefined;
    valueOfNetCollateralTransfer: number | undefined;
    usdProfitLoss: number;
}

export function calculateOraclePnlAtTime({
    timeFilteredEvents,
    initialCollateralTokenAmount,
    collateralTokenAmount,
    principalTokenAmount,
    inputPrice,
    principalPrice,
    position
}: {
    timeFilteredEvents: ParsedLoanEventExpanded[] | undefined;
    initialCollateralTokenAmount: number;
    collateralTokenAmount: number;
    principalTokenAmount: number;
    inputPrice?: number; // for orca this is principal price, otherwise collateral price
    principalPrice?: number;
    position: LoopPositionExpanded | undefined;
}) {
    if (!timeFilteredEvents || !position) return undefined;
    const netCollateralTransfer = getNetCollateralTransfers(position, timeFilteredEvents, initialCollateralTokenAmount);

    const additionalBorrows = calculateAdditionalBorrowsFromEvents(timeFilteredEvents);
    const additionalRepayments = calculateLoopRepayments(timeFilteredEvents);

    const collateralValueUsd = bsMath.mul(collateralTokenAmount, inputPrice);
    const principalValueUsd = bsMath.mul(principalTokenAmount, principalPrice);

    const npvUsd = bsMath.sub(collateralValueUsd, principalValueUsd);
    const npvTokenAmount = bsMath.div(npvUsd, inputPrice);

    return calculateOraclePnl({
        npvUsd: npvUsd ?? 0,
        npvTokenAmount,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        inputPrice,
        events: timeFilteredEvents
    });
}

export function calculatePrincipalDebtTransfers(position: LoopPositionExpanded | undefined) {
    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position?.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position?.loanExpanded.events);

    const valueOfAdditionalBorrows = bsMath.mul(
        additionalBorrows?.tokenAmount,
        additionalBorrows?.weightedAverageTokenAmountPrice
    );
    const valueOfAdditionalRepayments = bsMath.mul(
        additionalRepayments?.tokenAmount,
        additionalRepayments?.weightedAverageTokenAmountPrice
    );

    return {
        additionalBorrows,
        additionalRepayments,
        valueOfAdditionalBorrows,
        valueOfAdditionalRepayments,
        netPrincipalDebtTransferUsd: bsMath.sub(valueOfAdditionalBorrows, valueOfAdditionalRepayments),
        netPrincipalDebtTransferTokenAmount: bsMath.sub(
            additionalBorrows?.tokenAmount,
            additionalRepayments?.tokenAmount
        )
    };
}

export function calculateOracleProfitLossStats(
    position: LoopPositionExpanded | undefined
): TokenProfitLossValues | undefined {
    if (!position) return undefined;
    const { userLoopInfo, loanExpanded } = position;
    const collateralOracle = userLoopInfo?.collateralOracle;
    const collateral = getZcCollateral(loanExpanded);
    if (!userLoopInfo || !collateralOracle || !collateral) return undefined;
    const netCollateralTransfer = getNetCollateralTransfers(
        position,
        position.loanExpanded.events,
        userLoopInfo.initialCollateralAmount
    );

    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position.loanExpanded.events);

    const netPosition = getNetPosition(position);

    const npvTokenAmount = netPosition?.tokenNpv;

    const inputPrice = collateralOracle.usdPrice;

    const fees = getClaimableWhirlpoolFees(getWhirlpoolLoopCollateral(position));

    return calculateOraclePnl({
        npvUsd: netPosition?.usdNpv ?? 0,
        npvTokenAmount,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        inputPrice,
        events: position.loanExpanded.events,
        pendingEarnedFeesUsd: fees
    });
}

function calculateOraclePnl({
    npvUsd,
    npvTokenAmount,
    netCollateralTransfer,
    additionalBorrows,
    additionalRepayments,
    inputPrice, // this is how we are pricing the collateral. orca collateral can be priced in both principal and collateral
    events,
    isPrincipalNpvToken,
    pendingEarnedFeesUsd
}: {
    npvUsd: number;
    npvTokenAmount: number | undefined;
    netCollateralTransfer: EventTokenChangeSummary | undefined;
    additionalBorrows: EventTokenChangeSummary | undefined;
    additionalRepayments: EventTokenChangeSummary | undefined;
    inputPrice: number | undefined;
    events: ParsedLoanEventExpanded[];
    isPrincipalNpvToken?: boolean;
    pendingEarnedFeesUsd?: number;
}) {
    const valueOfNetCollateralTransfer = bsMath.mul(
        netCollateralTransfer?.tokenAmount,
        netCollateralTransfer?.weightedAverageTokenAmountPrice
    );

    const valueOfAdditionalBorrows = bsMath.mul(
        additionalBorrows?.tokenAmount,
        additionalBorrows?.weightedAverageTokenAmountPrice
    );

    const valueOfAdditionalRepayments = bsMath.mul(
        additionalRepayments?.tokenAmount,
        additionalRepayments?.weightedAverageTokenAmountPrice
    );

    const netAdditionalPrincipalValue = bsMath.sub(valueOfAdditionalBorrows, valueOfAdditionalRepayments);

    if (inputPrice === undefined || inputPrice === 0) {
        return {
            netCollateralTransfer,
            additionalBorrows,
            additionalRepayments,
            profitLossTokenAmount: undefined,
            valueOfAdditionalBorrows,
            valueOfAdditionalRepayments,
            valueOfNetCollateralTransfer,
            usdProfitLoss: 0
        };
    }
    const collateralTokenAmountOfAdditionalBorrows = bsMath.div(netAdditionalPrincipalValue, inputPrice);
    const feesTokenAmount = bsMath.div(pendingEarnedFeesUsd, inputPrice);

    const lastCloseEvents = getLastCloseEvents(events);
    const lastRepayPrincipalEvent = lastCloseEvents?.lastRepayPrincipalEvent;

    const usdProfitLoss = bsMath.sub(bsMath.add(npvUsd, netAdditionalPrincipalValue), valueOfNetCollateralTransfer);
    const usdProfitLossWithFees = bsMath.add(usdProfitLoss, pendingEarnedFeesUsd ?? 0);

    // HISTORICAL ONLY npvTokenAmount can be in principal or collateral. so if we are subtracting net flows, we need to convert it all to dollars, and the back into the principal price at swap
    const profitLossTokenAmount = isPrincipalNpvToken
        ? bsMath.div(usdProfitLossWithFees, lastRepayPrincipalEvent?.usdPrice)
        : bsMath.sub(
              bsMath.add(bsMath.add(npvTokenAmount, feesTokenAmount ?? 0), collateralTokenAmountOfAdditionalBorrows),
              netCollateralTransfer?.tokenAmount
          );

    return {
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        profitLossTokenAmount,
        valueOfAdditionalBorrows,
        valueOfAdditionalRepayments,
        valueOfNetCollateralTransfer,
        usdProfitLoss: usdProfitLossWithFees ?? 0
    };
}

export function calculateHistoricalOracleProfitLossStats(
    position: LoopPositionExpanded
): TokenProfitLossValues | undefined {
    // if the loop has a withdrawal info, we can use that to get the net position value
    // otherwise we can imply the net position value from the last two close events

    const historicalNpv = position.userLoopInfo.withdrawalInfo
        ? getHistoricalNpvFromWithdrawalInfo(position)
        : getImpliedNpvBeforeCloseEvents(position);

    const netCollateralTransfer = getNetCollateralTransfers(
        position,
        position.loanExpanded.events,
        position.userLoopInfo?.initialCollateralAmount,
        true
    );
    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position?.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position?.loanExpanded.events, true);

    return calculateOraclePnl({
        npvUsd: historicalNpv?.npvUsd ?? 0,
        npvTokenAmount: historicalNpv?.npvTokenAmount ?? 0,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        inputPrice: position.userLoopInfo?.collateralOracle?.usdPrice,
        events: position.loanExpanded.events,
        isPrincipalNpvToken:
            position.userLoopInfo.withdrawalInfo?.withdrawnTokenMint === position.loopExpanded?.principalToken.mint
    });
}

export function calculateLoopOracleProfitLoss(position: LoopPositionExpanded): ProfitLossStats | undefined {
    const tokenProfitLoss = calculateOracleProfitLossStats(position);

    if (!tokenProfitLoss) return undefined;

    const { usdProfitLoss, profitLossTokenAmount } = tokenProfitLoss;

    return {
        profitLossUsd: usdProfitLoss,
        contributions: sumLoopPositionUsdContributions(position),
        profitLossTokenAmount: profitLossTokenAmount ?? 0
    };
}

export function sumLoopPositionUsdContributions(loopPosition: LoopPositionExpanded) {
    // cron updates rates every 15 minutes so oracle price might be more recent than userLoopInfo.initialCollateralPrice

    const isActive = isLoanActive(loopPosition.loanExpanded);

    // is !isActive we skip the last remove collateral event.
    // its closed so we dont want the last remove event to look like a partial withdrawal
    const collateralDeposits = getNetCollateralTransfers(
        loopPosition,
        loopPosition.loanExpanded.events,
        loopPosition.userLoopInfo.initialCollateralAmount,
        !isActive
    );

    const repayments = calculateLoopRepayments(loopPosition.loanExpanded.events);

    const { initialDepositAmount, initialDepositAmountUsd } = getInitialLoopDepositInfo(loopPosition);

    return {
        initialCollateralUsd: initialDepositAmountUsd ?? 0,
        initialCollateralAmount: initialDepositAmount,
        totalCollateralTopUpsUsd: (collateralDeposits?.tokenAmountUsd ?? 0) - (initialDepositAmountUsd ?? 0),
        totalCollateralTopUpsAmount: (collateralDeposits?.tokenAmount ?? 0) - initialDepositAmount,
        totalCollateralContributionAmount: collateralDeposits?.tokenAmount ?? 0,
        repaymentsUsd: repayments?.tokenAmountUsd ?? 0,
        totalContributionUsd:
            (initialDepositAmountUsd ?? 0) +
            (collateralDeposits?.tokenAmountUsd ?? 0) +
            (repayments?.tokenAmountUsd ?? 0),
        totalPaidUsd: repayments?.tokenAmountUsd ?? 0,
        additionalDepositsUsd: (collateralDeposits?.tokenAmountUsd ?? 0) + (repayments?.tokenAmountUsd ?? 0)
    };
}

export function isLstLoop(loop: LoopExpanded) {
    return loop.collateralToken.tags?.includes(TokenListTag.LST) && loop.principalToken.mint === WRAPPED_SOL_MINT;
}

export function isExponentLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Exponent;
}

export function isRatexLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Ratex;
}

export function isAdrenaLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Adrena;
}

export function isMeteora(loopExpanded: LoopExpanded): loopExpanded is LoopExpandedMeteora {
    return "meteoraPool" in loopExpanded && "tokenA" in loopExpanded && "tokenB" in loopExpanded;
}

export function isWhirlpoolLoop(loopExpanded: LoopExpanded | undefined): loopExpanded is LoopExpandedOrca {
    return !!loopExpanded && "whirlpool" in loopExpanded;
}

export function getWhirlpoolLoopCollateral(
    loopPosition: LoopPositionExpanded | undefined
): WhirlpoolPositionExpanded | undefined {
    if (
        loopPosition &&
        isWhirlpoolLoop(loopPosition.loopExpanded) &&
        loopPosition.loanExpanded.collateral.length > 0 &&
        loopPosition.loanExpanded.collateral[0].clmmPosition
    ) {
        return loopPosition.loanExpanded.collateral[0].clmmPosition as WhirlpoolPositionExpanded;
    }
    return undefined;
}

interface OrcaCollateralConversion {
    collateralCTokenInUsd: number | undefined;
    collateralPTokenInUsd: number | undefined;
    totalCollateralInUsd: number | undefined;
    collateralCTokenInPrincipalToken: number | undefined;
    totalCollateralInPrincipalToken: number | undefined;
    collateralCTokenAmount: number | undefined;
    collateralPTokenAmount: number | undefined;
    collateralCTokenMetadata: TokenListMetadata | undefined;
    collateralPTokenMetadata: TokenListMetadata | undefined;
    initialCollateralAmount: number | undefined;
    totalInitialized: number | undefined;
}

export function orcaCollateralPrincipalInfo(
    loopPosition: LoopPositionExpanded | undefined
): OrcaCollateralConversion | undefined {
    if (!loopPosition) return undefined;
    if (!isWhirlpoolLoop(loopPosition.loopExpanded)) return undefined;

    const [collateralPToken, collateralCToken] = getOrcaCollateralTokenAmounts(loopPosition);

    const collateralCTokenInUsd = bsMath.mul(collateralCToken?.amount, collateralCToken?.usdPrice);
    const collateralPTokenInUsd = bsMath.mul(collateralPToken?.amount, collateralPToken?.usdPrice);
    const totalCollateralInUsd = bsMath.add(collateralCTokenInUsd, collateralPTokenInUsd);

    const { leveragedAmount, initialDepositAmount } = getInitialLoopDepositInfo(loopPosition);

    // convert collateral to principal
    const collateralCTokenInPrincipalToken = bsMath.div(collateralCTokenInUsd, collateralPToken?.usdPrice);
    const totalCollateralInPrincipalToken = bsMath.add(collateralCTokenInPrincipalToken, collateralPToken?.amount);

    const getAdditionalCollateralDeposits = calculateAdditionalCollateralDeposits(
        loopPosition.loanExpanded.events,
        loopPosition
    );

    const withdrawals = calculateWithdrawalsFromEvents(loopPosition.loanExpanded.events, loopPosition);

    const totalInitialized = bsMath.add(
        bsMath.add(leveragedAmount, initialDepositAmount),
        bsMath.sub(getAdditionalCollateralDeposits?.tokenAmount ?? 0, withdrawals?.tokenAmount ?? 0)
    );

    return {
        collateralCTokenInUsd,
        collateralPTokenInUsd,
        totalCollateralInUsd,
        collateralCTokenInPrincipalToken,
        totalCollateralInPrincipalToken,
        collateralCTokenAmount: collateralCToken?.amount,
        collateralPTokenAmount: collateralPToken?.amount,
        collateralCTokenMetadata: collateralCToken?.metadata,
        collateralPTokenMetadata: collateralPToken?.metadata,
        initialCollateralAmount: loopPosition.userLoopInfo.initialCollateralAmount,
        totalInitialized
    };
}

export function useLatestCollateralYieldHistoryByMint(collateralMints: string[] | undefined) {
    const { data: collateralYield, isLoading } = useLoopCollateralYieldLatestByMintsQuery(
        collateralMints ? collateralMints : skipToken
    );

    const memoizedData = useMemo(() => collateralYield, [collateralYield]);

    return {
        data: memoizedData,
        isLoading
    };
}

export function useGetCollateralYieldHistory(collateralMints: string[], startTimestamp: number, endTimestamp: number) {
    const roundedStartTimestamp = roundTimestamp(startTimestamp);
    const roundedEndTimestamp = roundTimestamp(endTimestamp, { roundUp: true });

    const { data: collateralYields, isLoading } = useCollateralYieldHistoryQuery(
        {
            startTime: roundedStartTimestamp,
            endTime: roundedEndTimestamp,
            collateralMints: collateralMints
        },
        { skip: collateralMints.length === 0 }
    );

    return {
        data: collateralYields,
        isLoading
    };
}

export function useGetCollateralYieldHistoryFlat(
    collateralMints: string[],
    startTimestamp: number,
    endTimestamp: number
) {
    const roundedStartTimestamp = roundTimestamp(startTimestamp);
    const roundedEndTimestamp = roundTimestamp(endTimestamp, { roundUp: true });
    const { data: collateralYields, isLoading } = useCollateralYieldHistoryQuery(
        {
            startTime: roundedStartTimestamp,
            endTime: roundedEndTimestamp,
            collateralMints: collateralMints
        },
        { skip: collateralMints.length === 0 }
    );

    const collateralYieldsFlat = [];

    for (const collateralMint of collateralMints) {
        const collateralYield = collateralYields?.[collateralMint];
        if (collateralYield) {
            collateralYieldsFlat.push(...collateralYield);
        }
    }

    return {
        data: collateralYieldsFlat,
        isLoading
    };
}

//ParsedOrcaWhirlpool[] - this gets the nft mints for the whirlpool
export function getWhirlpoolLoopNftMints(position: LoopPositionExpanded | undefined): string[] | undefined {
    if (!position) return undefined;
    if (!isWhirlpoolLoop(position.loopExpanded)) return undefined;

    const [collateralPToken, collateralCToken] = getOrcaCollateralTokens(position);

    const events = position.loanExpanded.events;
    const sortedEvents = [...events]
        .sort((a, b) => a.eventTime - b.eventTime)
        .filter((e) => e.action === LoanEventActionType.AddCollateral);

    const addCollateralEventMints = sortedEvents.filter(
        (e) =>
            e.action === LoanEventActionType.AddCollateral &&
            e.assetIdentifier !== collateralCToken?.mint &&
            e.assetIdentifier !== collateralPToken?.mint
    );

    return addCollateralEventMints.map((e) => e.assetMint).filter(filterTruthy);
}

export function useCalculateLstBreakevenTime({
    totalCollateralUsd,
    contributedCollateralUsd,
    totalDebtUsd,
    feesUsd,
    lstApy,
    loanApy
}: {
    totalCollateralUsd: number | undefined;
    contributedCollateralUsd: number | undefined;
    totalDebtUsd: number | undefined;
    feesUsd: number | undefined;
    lstApy: number | undefined;
    loanApy: number | undefined;
}): number | undefined {
    const { totalEpochDurationSeconds } = useEpochInfo();

    return useMemo(() => {
        if (
            totalCollateralUsd === undefined ||
            lstApy === undefined ||
            totalDebtUsd === undefined ||
            loanApy === undefined ||
            totalEpochDurationSeconds === undefined ||
            contributedCollateralUsd === undefined ||
            lstApy < 0 ||
            lstApy < loanApy
        ) {
            return undefined;
        }

        const secondsInYear = 365 * 24 * 60 * 60;
        const secondsInMonths = 30 * 24 * 60 * 60; // to return early if loop hasnt broken even in a month's time

        const lstRatePerEpoch = lstApy * (totalEpochDurationSeconds / secondsInYear);
        const debtRatePerEpoch = loanApy * (totalEpochDurationSeconds / secondsInYear);

        let cumulativeDebtUsd = totalDebtUsd ?? 0;
        let cumulativeCollateralUsd = totalCollateralUsd;

        // Simulate forward
        let duration = 0;

        // Add feesUsd to the break-even calculation as a fixed cost (does not compound)
        const fixedFeesUsd = feesUsd ?? 0;

        while (duration < secondsInMonths) {
            duration += totalEpochDurationSeconds;
            const rewardThisEpochUsd = cumulativeCollateralUsd * lstRatePerEpoch;
            const debtThisEpochUsd = cumulativeDebtUsd * debtRatePerEpoch;

            cumulativeCollateralUsd += rewardThisEpochUsd;
            cumulativeDebtUsd += debtThisEpochUsd;
            const breakEven = cumulativeDebtUsd + contributedCollateralUsd + fixedFeesUsd;

            if (cumulativeCollateralUsd >= breakEven) {
                break;
            }
        }
        return duration >= secondsInMonths ? undefined : duration + totalEpochDurationSeconds;
    }, [
        totalCollateralUsd,
        contributedCollateralUsd,
        totalDebtUsd,
        feesUsd,
        lstApy,
        loanApy,
        totalEpochDurationSeconds
    ]);
}

export interface EventTokenChangeSummary {
    tokenAmount: number;
    tokenAmountUsd: number;
    weightedAverageTokenAmountPrice: number;
}

function convertCollateralToPrincipalTokenChangeEvent(
    collateralTokenChange: EventTokenChangeSummary | undefined,
    principalTokenChange: EventTokenChangeSummary | undefined
): EventTokenChangeSummary | undefined {
    // Convert collateral token amounts to principal token equivalents
    const collateralTokenInPrincipalToken = bsMath.div(
        collateralTokenChange?.tokenAmount ?? 0,
        principalTokenChange?.weightedAverageTokenAmountPrice ?? 1
    );

    const collateralTokenInPrincipalTokenUsd = bsMath.div(
        collateralTokenChange?.tokenAmountUsd ?? 0,
        principalTokenChange?.weightedAverageTokenAmountPrice ?? 1
    );

    // Combine the stats into one unified EventTokenChangeSummary
    const combinedTokenChangeInPrincipal: EventTokenChangeSummary = {
        tokenAmount: bsMath.add(principalTokenChange?.tokenAmount, collateralTokenInPrincipalToken) ?? 0,
        tokenAmountUsd: bsMath.add(principalTokenChange?.tokenAmountUsd, collateralTokenInPrincipalTokenUsd) ?? 0,
        weightedAverageTokenAmountPrice:
            bsMath.div(
                bsMath.add(principalTokenChange?.tokenAmountUsd, collateralTokenInPrincipalTokenUsd),
                bsMath.add(principalTokenChange?.tokenAmount, collateralTokenInPrincipalToken)
            ) ?? 0
    };

    return combinedTokenChangeInPrincipal;
}

function calculateOrcaWithdrawalsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const [collateralPToken, collateralCToken] = getOrcaCollateralTokens(position);

    // Aggregate the token amount changes for each collateral type
    const removePCollateralEvents = events?.filter(
        (e) => e.action === LoanEventActionType.RemoveCollateral && e.assetIdentifier === collateralPToken?.mint
    );
    const removeCCollateralEvents = events?.filter(
        (e) => e.action === LoanEventActionType.RemoveCollateral && e.assetIdentifier === collateralCToken?.mint
    );

    const removePCollateralTokenChange = calculateTokenChangesFromEvents(
        removePCollateralEvents,
        LoanEventActionType.RemoveCollateral
    );
    const removeCCollateralTokenChange = calculateTokenChangesFromEvents(
        removeCCollateralEvents,
        LoanEventActionType.RemoveCollateral
    );

    // Use the utility function to convert and combine the token changes
    return convertCollateralToPrincipalTokenChangeEvent(removeCCollateralTokenChange, removePCollateralTokenChange);
}

export function calculateWithdrawalsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const isOrca = isWhirlpoolLoop(position?.loopExpanded);
    if (isOrca) {
        return calculateOrcaWithdrawalsFromEvents(events, position);
    }

    return calculateTokenChangesFromEvents(events, LoanEventActionType.RemoveCollateral);
}

export function calculateAdditionalBorrowsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, LoanEventActionType.BorrowPrincipal, { skipFirstEvent: true });
}

export function calculateLoopRepayments(
    events: ParsedLoanEventExpanded[] | undefined,
    isHistorical = false
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, LoanEventActionType.RepayPrincipal, { skipLastEvent: isHistorical });
}

export function getOrcaCollateralTokens(position: LoopPositionExpanded | undefined) {
    if (!isWhirlpoolLoop(position?.loopExpanded)) {
        return [undefined, undefined];
    }

    const tokenA = position?.loopExpanded?.whirlpool.tokenA;
    const tokenB = position?.loopExpanded?.whirlpool.tokenB;

    return tokenA?.mint === position?.loopExpanded?.principalToken.mint ? [tokenA, tokenB] : [tokenB, tokenA];
}

export function getOrcaCollateralTokenAmounts(position: LoopPositionExpanded | undefined) {
    const loopCollateral = getWhirlpoolLoopCollateral(position);

    const [collateralPToken, collateralCToken] =
        loopCollateral?.tokenA.mint === position?.loopExpanded?.principalToken.mint
            ? [loopCollateral?.tokenA, loopCollateral?.tokenB]
            : [loopCollateral?.tokenB, loopCollateral?.tokenA];

    return [collateralPToken, collateralCToken];
}

function calculateOrcaAdditionalCollateralDeposits(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const [collateralPToken, collateralCToken] = getOrcaCollateralTokenAmounts(position);
    if (!collateralPToken || !collateralCToken) {
        return undefined;
    }

    const addPCollateralEvents = events?.filter(
        (e) => e.action === LoanEventActionType.AddCollateral && e.assetIdentifier === collateralPToken?.mint
    );
    const addCCollateralEvents = events?.filter(
        (e) => e.action === LoanEventActionType.AddCollateral && e.assetIdentifier === collateralCToken?.mint
    );

    const addPCollateralTokenChange = calculateTokenChangesFromEvents(
        addPCollateralEvents,
        LoanEventActionType.AddCollateral
    );

    const addCCollateralTokenChange = calculateTokenChangesFromEvents(
        addCCollateralEvents,
        LoanEventActionType.AddCollateral
    );

    // Use the utility function to convert and combine the token changes
    return convertCollateralToPrincipalTokenChangeEvent(addCCollateralTokenChange, addPCollateralTokenChange);
}

// Skip first add collateral event - it's the loop start (initial + leveraged amounts), not an additional deposit
export function calculateAdditionalCollateralDeposits(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const isOrca = isWhirlpoolLoop(position?.loopExpanded);
    if (isOrca) {
        const res = calculateOrcaAdditionalCollateralDeposits(events, position);
        return res;
    }

    return calculateTokenChangesFromEvents(events, LoanEventActionType.AddCollateral, { skipFirstEvent: true });
}

export interface NetCollateralTransfers {
    tokenAmount: number;
    tokenAmountUsd: number;
    weightedAverageTokenAmountPrice: number;
}

export interface NetOrcaCollateralTransfers extends NetCollateralTransfers {
    weightedAverageCollateralTokenAmountPrice: number;
    weightedAveragePrincipalTokenAmountPrice: number;
    collateralTokenAmount: number;
    principalTokenAmount: number;
    collateralTokenAmountUsd: number;
    principalTokenAmountUsd: number;
}

export function getOrcaNetCollateralTransfersfromEvents({
    events,
    initialCollateralAmount,
    loopPositionExpanded
}: {
    events: ParsedLoanEventExpanded[] | undefined;
    initialCollateralAmount: number | undefined;
    loopPositionExpanded: LoopPositionExpanded | undefined;
}): NetOrcaCollateralTransfers | undefined {
    if (!events) return undefined;

    const principalPrice = loopPositionExpanded?.loopExpanded?.principalOracle?.usdPrice;

    const [collateralPToken, collateralCToken] = getOrcaCollateralTokens(loopPositionExpanded);

    const sortedEvents = [...(events ?? [])].sort((a, b) => a.eventTime - b.eventTime);
    const firstBorrowEvent = sortedEvents.find((e) => e.action === LoanEventActionType.BorrowPrincipal); // used for principal price;
    const filteredEvents = sortedEvents.filter(
        (e) =>
            (e.action === LoanEventActionType.AddCollateral || e.action === LoanEventActionType.RemoveCollateral) &&
            (e.assetIdentifier === collateralPToken?.mint || e.assetIdentifier === collateralCToken?.mint)
    );

    const totalEvents = filteredEvents.reduce(
        (acc, event) => {
            const isPrincipalMint = event.eventAssetMetadata?.mint === collateralPToken?.mint;

            const tokenAmount = event.amount ?? 0;
            const eventPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = bsMath.mul(tokenAmount, eventPrice) ?? 0;
            const tokenAmountNormlizeToPrincipal = bsMath.div(tokenAmountUsd, principalPrice) ?? 0;

            if (event.action === LoanEventActionType.AddCollateral) {
                return {
                    tokenAmount: acc.tokenAmount + tokenAmountNormlizeToPrincipal,
                    tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0,
                    weightedAverageCollateralTokenAmountPrice: 0,
                    weightedAveragePrincipalTokenAmountPrice: 0,
                    collateralTokenAmount: isPrincipalMint
                        ? acc.collateralTokenAmount
                        : acc.collateralTokenAmount + tokenAmount,
                    principalTokenAmount: isPrincipalMint
                        ? acc.principalTokenAmount + tokenAmountNormlizeToPrincipal
                        : acc.principalTokenAmount,
                    collateralTokenAmountUsd: isPrincipalMint
                        ? acc.collateralTokenAmountUsd
                        : acc.collateralTokenAmountUsd + tokenAmountUsd,
                    principalTokenAmountUsd: isPrincipalMint
                        ? acc.principalTokenAmountUsd + tokenAmountUsd
                        : acc.principalTokenAmountUsd
                };
            } else {
                return {
                    tokenAmount: acc.tokenAmount - tokenAmountNormlizeToPrincipal,
                    tokenAmountUsd: acc.tokenAmountUsd - tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0,
                    weightedAverageCollateralTokenAmountPrice: 0,
                    weightedAveragePrincipalTokenAmountPrice: 0,
                    collateralTokenAmount: isPrincipalMint
                        ? acc.collateralTokenAmount
                        : acc.collateralTokenAmount - tokenAmount,
                    principalTokenAmount: isPrincipalMint
                        ? acc.principalTokenAmount - tokenAmountNormlizeToPrincipal
                        : acc.principalTokenAmount,
                    collateralTokenAmountUsd: isPrincipalMint
                        ? acc.collateralTokenAmountUsd
                        : acc.collateralTokenAmountUsd - tokenAmountUsd,
                    principalTokenAmountUsd: isPrincipalMint
                        ? acc.principalTokenAmountUsd - tokenAmountUsd
                        : acc.principalTokenAmountUsd
                };
            }
        },
        {
            tokenAmount: 0,
            tokenAmountUsd: 0,
            weightedAverageTokenAmountPrice: 0,
            weightedAverageCollateralTokenAmountPrice: 0,
            weightedAveragePrincipalTokenAmountPrice: 0,
            collateralTokenAmount: 0,
            principalTokenAmount: 0,
            collateralTokenAmountUsd: 0,
            principalTokenAmountUsd: 0
        }
    );

    totalEvents.principalTokenAmount += initialCollateralAmount ?? 0;
    totalEvents.principalTokenAmountUsd += bsMath.mul(firstBorrowEvent?.usdPrice, initialCollateralAmount) ?? 0;

    totalEvents.tokenAmount = bsMath.add(initialCollateralAmount, totalEvents.tokenAmount) ?? 0;
    totalEvents.tokenAmountUsd += bsMath.mul(firstBorrowEvent?.usdPrice, initialCollateralAmount) ?? 0;

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
        totalEvents.weightedAverageCollateralTokenAmountPrice =
            bsMath.div(totalEvents.collateralTokenAmountUsd, totalEvents.collateralTokenAmount) ?? 0;
        totalEvents.weightedAveragePrincipalTokenAmountPrice =
            bsMath.div(totalEvents.principalTokenAmountUsd, totalEvents.principalTokenAmount) ?? 0;
    }

    return totalEvents;
}

export function getNetCollateralTransfers(
    loopPositionExpanded: LoopPositionExpanded | undefined,
    events: ParsedLoanEventExpanded[] | undefined, // we have events as an arg because we sometime pass in time filtered events for the charts
    initialCollateralAmount: number | undefined,
    isHistorical = false
): NetCollateralTransfers | NetOrcaCollateralTransfers | undefined {
    if (!loopPositionExpanded) return undefined;
    if (isWhirlpoolLoop(loopPositionExpanded.loopExpanded)) {
        return getOrcaNetCollateralTransfersfromEvents({
            events,
            initialCollateralAmount,
            loopPositionExpanded
        });
    }
    return getNetCollateralTransfersfromEvents(events, initialCollateralAmount, isHistorical);
}

function getNetCollateralTransfersfromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    initialCollateralAmount: number | undefined,
    isHistorical = false
): NetCollateralTransfers | undefined {
    if (!events) return undefined;

    const sortedEvents = [...events].sort((a, b) => a.eventTime - b.eventTime);
    const filteredEvents = sortedEvents.filter(
        (e) => e.action === LoanEventActionType.AddCollateral || e.action === LoanEventActionType.RemoveCollateral
    );

    // If historical, exclude the last remove collateral event
    if (isHistorical) {
        const lastRemoveIndex = [...filteredEvents]
            .reverse()
            .findIndex((e) => e.action === LoanEventActionType.RemoveCollateral);
        if (lastRemoveIndex !== -1) {
            filteredEvents.splice(filteredEvents.length - 1 - lastRemoveIndex, 1);
        }
    }

    const firstAddCollateralEvent = filteredEvents.findIndex((e) => e.action === LoanEventActionType.AddCollateral);
    const firstRemovedCollateralEvent = filteredEvents.splice(firstAddCollateralEvent, 1)[0];

    const totalEvents = filteredEvents.reduce(
        (acc, event) => {
            const tokenAmount = event.amount ?? 0;
            const eventPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = bsMath.mul(tokenAmount, eventPrice) ?? 0;

            if (event.action === LoanEventActionType.AddCollateral) {
                return {
                    tokenAmount: acc.tokenAmount + tokenAmount,
                    tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0
                };
            } else {
                return {
                    tokenAmount: acc.tokenAmount - tokenAmount,
                    tokenAmountUsd: acc.tokenAmountUsd - tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0
                };
            }
        },
        { tokenAmount: 0, tokenAmountUsd: 0, weightedAverageTokenAmountPrice: 0 }
    );

    totalEvents.tokenAmount = bsMath.add(initialCollateralAmount, totalEvents.tokenAmount) ?? 0;
    totalEvents.tokenAmountUsd += bsMath.mul(firstRemovedCollateralEvent?.usdPrice, initialCollateralAmount) ?? 0;

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
    }

    return totalEvents;
}

export function calculateTokenChangesFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    actionType: LoanEventActionType,
    skipOptions: { skipFirstEvent?: boolean; skipLastEvent?: boolean } = { skipFirstEvent: false, skipLastEvent: false }
    // for some callers need to skip the first or last event
    // either for manual calculations or historical calculations
): EventTokenChangeSummary | undefined {
    if (!events) return undefined;

    const sortedEvents = [...events].sort((a, b) => a.eventTime - b.eventTime);
    const filteredEvents = sortedEvents.filter((e) => e.action === actionType);

    const totalEvents = filteredEvents.reduce(
        (acc, event, index) => {
            if (skipOptions.skipFirstEvent && index === 0) {
                return acc;
            }
            if (skipOptions.skipLastEvent && index === filteredEvents.length - 1) {
                return acc;
            }
            const tokenAmount = event.amount ?? 0;
            const tokenPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = event.amount * tokenPrice;

            return {
                tokenAmount: acc.tokenAmount + tokenAmount,
                tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                weightedAverageTokenAmountPrice: 0
            };
        },
        { tokenAmount: 0, tokenAmountUsd: 0, weightedAverageTokenAmountPrice: 0 }
    );

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
    }

    return totalEvents;
}

function generatePrincipalLiquidatedInfo(event: ParsedLoanEventExpanded): number {
    const data = event.actionMetadata[LoanEventActionType.Liquidate];
    const { preRepaymentPrincipalLedgerState, postRepaymentPrincipalLedgerState } = data.repaymentEventMetadata;
    const principalDelta = Math.abs(
        postRepaymentPrincipalLedgerState.principalRepaid - preRepaymentPrincipalLedgerState.principalRepaid
    );
    // const interestDelta = Math.abs(
    //     postRepaymentPrincipalLedgerState.interestDue - preRepaymentPrincipalLedgerState.interestDue
    // );
    return principalDelta; //+ interestDelta;
}

export function getLoopFirstAddCollateralEvent( // audit to make sure this works for orca
    events: ParsedLoanEventExpanded[] | undefined
): ParsedLoanEventExpanded | undefined {
    const sortedEvents = events?.sort((a, b) => a.eventTime - b.eventTime);
    const depositEvent = sortedEvents?.find((e) => e.action === LoanEventActionType.AddCollateral);
    return depositEvent;
}
export function getLoopFirstBorrowEvent(
    events: ParsedLoanEventExpanded[] | undefined
): ParsedLoanEventExpanded | undefined {
    const sortedEvents = events?.sort((a, b) => a.eventTime - b.eventTime);
    const depositEvent = sortedEvents?.find((e) => e.action === LoanEventActionType.BorrowPrincipal);
    return depositEvent;
}
interface TotalLoopLiquidationSummary {
    collateralLiquidated: number;
    collateralLiquidatedUsd: number;
    averageCollateralPrice: number;
    principalRepaidFromLiquidation: number;
    principalAmountUsd: number;
    averagePrincipalPrice: number;
}

export function calculateTotalLiquidations(
    events: ParsedLoanEventExpanded[] | undefined
): TotalLoopLiquidationSummary | undefined {
    const liquidationEvents = events?.filter((e) => isLiquidateEvent(e.action));
    const partialLiquidationEvents = liquidationEvents?.filter(
        (e) => e.actionMetadata[LoanEventActionType.Liquidate].liquidationType === LiquidationType.PartialLiquidation
    );
    const totalLiquidations = partialLiquidationEvents?.reduce(
        (acc, event) => {
            const collateralAmount = event.actionMetadata[LoanEventActionType.Liquidate].collateralTransfers[0].amount;
            const collateralPrice = event.actionMetadata[LoanEventActionType.Liquidate].collateralTransfers[0].price;
            const collateralAmountUsd = collateralPrice * collateralAmount;
            const principalRepaidFromLiquidation = generatePrincipalLiquidatedInfo(event);
            const principalPriceLiquidated = event.usdPrice ?? 0;
            const principalAmountUsd = principalRepaidFromLiquidation * principalPriceLiquidated;
            return {
                collateralLiquidated: acc.collateralLiquidated + collateralAmount,
                collateralLiquidatedUsd: acc.collateralLiquidatedUsd + collateralAmountUsd,
                averageCollateralPrice: 0,
                principalRepaidFromLiquidation: acc.principalRepaidFromLiquidation + principalRepaidFromLiquidation,
                principalAmountUsd: acc.principalAmountUsd + principalAmountUsd,
                averagePrincipalPrice: 0
            };
        },
        {
            collateralLiquidated: 0,
            collateralLiquidatedUsd: 0,
            averageCollateralPrice: 0,
            principalRepaidFromLiquidation: 0,
            principalAmountUsd: 0,
            averagePrincipalPrice: 0
        }
    );
    if (totalLiquidations && totalLiquidations.collateralLiquidated > 0) {
        totalLiquidations.averageCollateralPrice =
            totalLiquidations.collateralLiquidatedUsd / totalLiquidations.collateralLiquidated;
    }
    if (totalLiquidations && totalLiquidations.principalRepaidFromLiquidation > 0) {
        totalLiquidations.averagePrincipalPrice =
            totalLiquidations.principalAmountUsd / totalLiquidations.principalRepaidFromLiquidation;
    }
    return totalLiquidations;
}

export function calculateLeverageMultiplier(loopPositionExpanded: LoopPositionExpanded | undefined) {
    if (!loopPositionExpanded) return 0;

    const ltv = calculateLoanHealth(loopPositionExpanded.loanExpanded).ltv;

    const leverageMultiplier = 1 / (1 - ltv);

    return leverageMultiplier;
}

export function getInitialLoopDepositInfo(loopPositionExpanded: LoopPositionExpanded) {
    const userLoopInfo = loopPositionExpanded.userLoopInfo;

    const isWhirlpool = isWhirlpoolLoop(loopPositionExpanded.loopExpanded);

    const collateralMetadata = loopPositionExpanded.loopExpanded?.inputToken;

    const initialDepositAmount = userLoopInfo?.initialCollateralAmount;

    const initalAddCollateralEvent = getLoopFirstAddCollateralEvent(loopPositionExpanded.loanExpanded.events);
    const firstBorrowEvent = getLoopFirstBorrowEvent(loopPositionExpanded.loanExpanded.events);

    if (!isWhirlpool) {
        // Calculate leveraged collateral swap price
        let leveragedCollateralSwapPrice: number | undefined;
        if (firstBorrowEvent && initalAddCollateralEvent) {
            const borrowedAmount = firstBorrowEvent.amount;
            const swappedCollateralAmount = bsMath.sub(initalAddCollateralEvent.amount, initialDepositAmount ?? 0);
            const usdSpent = bsMath.mul(borrowedAmount, firstBorrowEvent.usdPrice);
            leveragedCollateralSwapPrice = bsMath.div(usdSpent, swappedCollateralAmount);
        }

        const initialDepositPrice = initalAddCollateralEvent?.usdPrice;
        const leveragedAmount = bsMath.sub(initalAddCollateralEvent?.amount, initialDepositAmount);

        return {
            leveragedAmount,
            leveragedPrice: leveragedCollateralSwapPrice,
            initialDepositAmount,
            initialDepositAmountUsd: bsMath.mul(initialDepositAmount, initialDepositPrice),
            collateralMetadata,
            initialDepositPrice
        };
    }

    const firstBorrowPrincipalPrice = firstBorrowEvent?.usdPrice;

    return {
        leveragedAmount: firstBorrowEvent?.amount,
        leveragedPrice: firstBorrowPrincipalPrice,
        initialDepositAmount,
        collateralMetadata,
        initialDepositPrice: firstBorrowPrincipalPrice,
        initialDepositAmountUsd: bsMath.mul(initialDepositAmount, firstBorrowPrincipalPrice)
    };
}

export function getNetPosition(loopPosition: LoopPositionExpanded | undefined) {
    if (!loopPosition) return undefined;

    const loopCollateral = getZcCollateral(loopPosition?.loanExpanded);
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const totalDebt = ledger?.ledgerDebt.total;

    if (isWhirlpoolLoop(loopPosition.loopExpanded)) {
        const orcaCollateral = orcaCollateralPrincipalInfo(loopPosition);

        return {
            tokenNpv: bsMath.sub(orcaCollateral?.totalCollateralInPrincipalToken, totalDebt),
            usdNpv: bsMath.sub(
                orcaCollateral?.totalCollateralInUsd,
                bsMath.mul(totalDebt, loopPosition?.loanExpanded?.principalUsdPrice)
            )
        };
    }
    const collateralPrice = loopPosition?.userLoopInfo?.collateralOracle?.usdPrice;
    const collateralInUsd = bsMath.mul(loopCollateral?.amount, collateralPrice);
    const totalDebtUsd = bsMath.mul(totalDebt, loopPosition?.loopExpanded?.principalOracle.usdPrice);
    const principalInCollateral = bsMath.div(totalDebtUsd, collateralPrice);

    return {
        tokenNpv: bsMath.sub(loopCollateral?.amount, principalInCollateral),
        usdNpv: bsMath.sub(collateralInUsd, totalDebtUsd)
    };
}

export const MIN_TIME_FOR_RECENT_START = TIME.MINUTE * 30;

export function useLoopRecentStartTime(loopPosition: LoopPositionExpanded | undefined): {
    recentStartTime: boolean;
    isLoading: boolean;
} {
    const now = getUnixTs();
    const isOrcaDeposit = isWhirlpoolLoopDeposit(loopPosition?.userLoopInfo, loopPosition?.loopExpanded);
    const whirlpoolCollateral = getWhirlpoolLoopCollateral(loopPosition);

    const { data: collateralYieldHistory, isLoading } = useGetCollateralYieldHistory(
        whirlpoolCollateral?.position.positionMint ? [whirlpoolCollateral.position.positionMint] : [],
        now - TIME.HOUR,
        now
    );

    if (!loopPosition) {
        return { recentStartTime: false, isLoading: true };
    }

    // Calculate if loop started recently based on time
    const timeSinceStart = now - loopPosition.loanExpanded?.loan.startTime;
    const isRecentByTime = Math.max(timeSinceStart, 0) < MIN_TIME_FOR_RECENT_START;

    const collateralYield = whirlpoolCollateral?.position.positionMint
        ? collateralYieldHistory?.[whirlpoolCollateral?.position.positionMint]?.length
        : undefined;

    // For Orca loops, also check if there's no yield history data
    const isRecentByYieldHistory = isOrcaDeposit && !collateralYield;

    return {
        recentStartTime: isRecentByTime || !!isRecentByYieldHistory,
        isLoading
    };
}

export function loopHasUnhandledEvents(loopPosition: LoopPositionExpanded | undefined): boolean {
    if (!loopPosition) {
        return false;
    }

    const isHistorical =
        loopPosition.loanExpanded.status !== LoanStatus.OngoingLoan &&
        loopPosition.loanExpanded.status !== LoanStatus.RefinanceGrace;

    const expectedEventCount = isHistorical ? 4 : 2;

    const eligibleActions = [LoanEventActionType.RefinanceLedger, LoanEventActionType.SellLedger];
    // The events a loan has undergone that don't have an impact on PnL
    const eligibleEvents = loopPosition.loanExpanded.events.filter((e) => {
        return !eligibleActions.includes(e.action);
    });

    return eligibleEvents.length !== expectedEventCount;
}

function getLastCloseEvents(events: ParsedLoanEventExpanded[] | undefined) {
    if (!events) return undefined;
    const sortedEvents = [...events].sort((a, b) => b.eventTime - a.eventTime);

    const lastRepayPrincipalEvent = sortedEvents.find((e) => e.action === LoanEventActionType.RepayPrincipal);
    const lastRemoveCollateralEvent = sortedEvents.find((e) => e.action === LoanEventActionType.RemoveCollateral);

    return {
        lastRepayPrincipalEvent,
        lastRemoveCollateralEvent
    };
}

function getImpliedNpvBeforeCloseEvents(position: LoopPositionExpanded): HistoricalNpv {
    const events = getLastCloseEvents(position.loanExpanded.events);

    const lastRepayPrincipalEvent = events?.lastRepayPrincipalEvent;
    const lastRemoveCollateralEvent = events?.lastRemoveCollateralEvent;

    const lastRepayPrincipalEventValue = bsMath.mul(lastRepayPrincipalEvent?.amount, lastRepayPrincipalEvent?.usdPrice);

    const collateralTokensUsedToRepayDebt = bsMath.div(
        lastRepayPrincipalEventValue,
        lastRemoveCollateralEvent?.usdPrice
    );

    const impliedTokenNpv = bsMath.sub(lastRemoveCollateralEvent?.amount, collateralTokensUsedToRepayDebt);

    const impliedUsdNpv = bsMath.mul(impliedTokenNpv, lastRemoveCollateralEvent?.usdPrice);

    return {
        npvTokenAmount: impliedTokenNpv,
        npvUsd: impliedUsdNpv
    };
}

export function isLoopPastMaturity(maturityDate: Date | undefined) {
    if (!maturityDate) return false;
    const now = getUnixTs();
    return maturityDate.getTime() / 1000 < now;
}

// Temporary function for getting the loop direction without using Loop Tags. Assumes if collateral mint is a stablecoin, the loop is a short on the principal token.
const STABLECOIN_MINTS = [USDC_MINT];
export function getLeverageLoopDirection(loopPosition: LoopExpanded | undefined): "Long" | "Short" | undefined {
    if (!loopPosition || !isLeverageLoop(loopPosition)) return undefined;
    if (!loopPosition.loopVault || !loopPosition.loopVault.collateralMint) return undefined;
    if (STABLECOIN_MINTS.includes(loopPosition.loopVault.collateralMint)) {
        return "Short";
    }
    return "Long";
}

export function useLoopTokenPriceChanges(
    mint: string | undefined,
    historicalPrices: OracleOHLC[] | undefined,
    periods: number[] = [TIME.DAY * 7]
): { data: (LoopTokenPriceChange | undefined)[]; loading: boolean } {
    const { now } = useCurrentTime(60_000); // 1 minute
    const sorted = useMemo(() => {
        if (!historicalPrices || !mint) return undefined;
        return [...historicalPrices].sort(
            (a, b) => roundTimestamp(a.startTimestamp) - roundTimestamp(b.startTimestamp)
        );
    }, [historicalPrices, mint]);

    const data: (LoopTokenPriceChange | undefined)[] = useMemo(() => {
        if (!mint || !sorted) return Array(periods.length).fill(undefined);

        return periods.map((period) => {
            const cutoff = now - period;

            const start = sorted.find((p) => roundTimestamp(p.startTimestamp) >= cutoff)?.avgPrice;
            const end = sorted[sorted.length - 1]?.avgPrice;

            if (start === undefined || end === undefined) return undefined;

            const change = (end - start) / start;
            return { start, end, change } as LoopTokenPriceChange;
        });
    }, [mint, sorted, periods, now]);

    return { data, loading: !historicalPrices };
}

export function useUnderlyingTokenInfo(tokenMints: string[]) {
    const { data: collateralYields, isLoading: collateralYieldsLoading } =
        useLoopCollateralYieldLatestByMintsQuery(tokenMints);

    const { getOracle, isLoading: oraclePricesLoading } = useOraclePrices(tokenMints);

    const isLoading = collateralYieldsLoading || oraclePricesLoading;

    // Convert to the format expected by getPositionYield and calculateNetApyLive
    const oraclePricesMap = useMemoizedMap(
        tokenMints,
        (mint) => mint,
        (mint) => getOracle(mint)
    );

    return {
        collateralYields,
        oraclePrices: oraclePricesMap,
        isLoading
    };
}

export function calculateLoopFairMarketValue({
    collateralAmount,
    debtAmount,
    collateralOraclePrice,
    principalOraclePrice
}: {
    collateralAmount: number | undefined;
    debtAmount: number | undefined;
    collateralOraclePrice: number | undefined;
    principalOraclePrice: number | undefined;
}): number | undefined {
    const oraclePrincipalUsd = bsMath.mul(principalOraclePrice, debtAmount);

    const oracleCollateralUsd = bsMath.mul(collateralOraclePrice, collateralAmount);

    const fairMarketValue = bsMath.sub(oracleCollateralUsd, oraclePrincipalUsd);

    return fairMarketValue;
}

function getTokenMintsFromClmmCollateral(loanExpanded: AbfLoanExpanded): string[] {
    return loanExpanded.collateral.flatMap((collateral: LoanCollateralExpanded) => {
        if (isWhirlpoolMetadata(collateral.metadata) || isRaydiumMetadata(collateral.metadata)) {
            const position = collateral.clmmPosition;
            return [position?.tokenA?.mint, position?.tokenB?.mint].filter(filterTruthy);
        }
        return [];
    });
}

export function isHistoricalPrincipalWithdrawal(loopPosition: LoopPositionExpanded | undefined): boolean {
    return (
        loopPosition?.userLoopInfo.withdrawalInfo?.withdrawnTokenMint ===
        loopPosition?.loopExpanded?.principalToken.mint
    );
}

export function getPandoraErrorMessage(error: FetchBaseQueryError | SerializedError, fallbackMessage: string): string {
    const errorCode = getPandoraErrorCode(error);
    switch (errorCode) {
        case PandoraErrorCodes.JUPITER_NO_ROUTES_FOUND:
            return "Unable to find a swap route through our integration partners.";
        default:
            return fallbackMessage;
    }
}

export function getPandoraErrorCode(error: FetchBaseQueryError | SerializedError): PandoraErrorCodes | undefined {
    if ("data" in error && error.data && typeof error.data === "object" && "code" in error.data) {
        if (typeof error.data.code === "number") {
            return error.data.code as PandoraErrorCodes;
        }
    }
}

export function isPrincipalTokenWind(principalTokenMint: string | undefined, inputMint: string | undefined) {
    return inputMint === principalTokenMint;
}
